import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  removeCachedVariables,
  upsertCachedVariable,
  upsertCachedVariables,
} from "@/features/template-builder/queries/cache";
import { templateBuilderMutations } from "@/features/template-builder/mutations";
import type {
  TemplateVariableInput,
  TemplateVariableTarget,
} from "@/domains/templates/types";

function mutationTarget(target: TemplateVariableTarget) {
  return target.level === "analysisstep"
    ? { templateId: target.templateId, stepId: target.stepId }
    : { templateId: target.templateId };
}

export function useTemplateVariableMutations(target: TemplateVariableTarget) {
  const queryClient = useQueryClient();
  const resourceTarget = mutationTarget(target);

  const createVariable = useMutation({
    mutationFn: ({ variable }: { variable: TemplateVariableInput }) =>
      templateBuilderMutations.createVariable({ ...resourceTarget, variable }),
    onSuccess: (variable) => {
      upsertCachedVariable(queryClient, target, variable);
    },
  });

  const updateVariable = useMutation({
    mutationFn: ({
      variableId,
      variable,
    }: {
      variableId: string;
      variable: TemplateVariableInput;
    }) =>
      templateBuilderMutations.updateVariable({
        ...resourceTarget,
        variableId,
        variable,
      }),
    onSuccess: (variable) => {
      upsertCachedVariable(queryClient, target, variable);
    },
  });

  const createVariableHierarchy = useMutation({
    mutationFn: ({ variable }: { variable: TemplateVariableInput }) =>
      templateBuilderMutations.createVariableHierarchy({
        ...resourceTarget,
        variable,
      }),
    onSuccess: (variables) => {
      upsertCachedVariables(queryClient, target, variables);
    },
  });

  const deleteVariable = useMutation({
    mutationFn: ({ variableId }: { variableId: string }) =>
      templateBuilderMutations.deleteVariable({
        ...resourceTarget,
        variableId,
      }),
    onSuccess: ({ deleted_variable_ids: variableIds }) => {
      removeCachedVariables(queryClient, target, variableIds);
    },
  });

  return {
    createVariable,
    createVariableHierarchy,
    updateVariable,
    deleteVariable,
  };
}
